import { api } from './api';
import type { Retailer } from './retailersApi';

/**
 * Products API wrappers.
 *
 * Backed by `GET /api/products/:id` (see api/src/routes/products.js). The
 * server returns the full normalized product joined with its aliases and
 * the per-retailer listings (each pre-joined with its `Retailer`).
 *
 * Decimal columns (price, pricePerRound, bulletWeightGrains, confidence
 * scores, etc.) arrive as strings from Sequelize/MySQL — consumers should
 * either keep them as strings for display or coerce with `Number()` when
 * doing math/sorting. The `pricePerRoundNumber()` helper covers the common
 * sorting case.
 */

export interface ProductAlias {
  id: string;
  normalizedProductId: string;
  alias: string;
  source: 'scraper' | 'reviewer' | 'seed';
}

export interface NormalizedProduct {
  id: string;
  slug?: string | null;
  brand: string | null;
  productLine: string | null;
  caliber: string | null;
  /** Taxonomy-derived ammo category (handgun/rifle/rimfire/shotgun) — #233. */
  ammoType?: string | string[] | null;
  bulletWeightGrains: number | string | null;
  bulletType: string | null;
  caseMaterial: string | null;
  primerType: string | null;
  useCases: string[] | null;
  isReloaded: boolean | null;
  isSubsonic: boolean | null;
  isLeadFree: boolean | null;
  upc: string | null;
  manufacturerPartNumber: string | null;
  confidenceScores: Record<string, number | string> | null;
  confidenceScore: number | string | null;
}

export type DataFreshness = 'fresh' | 'aging' | 'stale' | 'unavailable';
export type StockStatus = 'in_stock' | 'out_of_stock' | 'limited' | 'unknown';

export interface ProductListing {
  id: string;
  retailerId: string;
  normalizedProductId?: string;
  sourceUrl: string;
  rawTitle: string | null;
  price: number | string | null;
  currency: string;
  roundCount: number | null;
  pricePerRound: number | string | null;
  salePrice?: number | string | null;
  stockStatus: StockStatus;
  stockQuantity: number | null;
  shippingEstimate?: number | string | null;
  taxEstimate?: number | string | null;
  lotNumber: string | null;
  dataFreshness: DataFreshness;
  lastScrapedAt: string | null;
  lastVerifiedAt: string | null;
  confidenceScore: number | string | null;
  Retailer?: Retailer;
}

export interface ProductDetailResponse {
  product: NormalizedProduct & { aliases?: ProductAlias[] };
  listings: ProductListing[];
}

/**
 * A single price/stock observation in a time-series snapshot.
 * `price` and `pricePerRound` arrive as strings from Sequelize DECIMAL columns.
 */
export interface PriceHistoryPoint {
  price: string | null;
  pricePerRound: string | null;
  currency: string;
  stockStatus: StockStatus;
  observedAt: string;
}

/** All snapshots for one retailer listing. */
export interface PriceHistorySeries {
  productListingId: string;
  retailerId: string;
  retailerName: string;
  points: PriceHistoryPoint[];
}

/** Response shape from `GET /api/products/:id/price-history`. */
export interface PriceHistoryResponse {
  productId: string;
  slug: string | null;
  series: PriceHistorySeries[];
}

/**
 * Coerce a possibly-string decimal to a Number. Returns `Number.POSITIVE_INFINITY`
 * for null/undefined so sort comparators push missing values to the end.
 */
export function pricePerRoundNumber(listing: ProductListing): number {
  const raw = listing.pricePerRound;
  if (raw === null || raw === undefined) return Number.POSITIVE_INFINITY;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

export const productsApi = {
  getProduct(id: string): Promise<ProductDetailResponse> {
    return api.get<ProductDetailResponse>(`/products/${encodeURIComponent(id)}`);
  },

  /**
   * Fetch snapshot-based price history for a product.
   * Backed by `GET /api/products/:id/price-history`.
   */
  getPriceHistory(id: string): Promise<PriceHistoryResponse> {
    return api.get<PriceHistoryResponse>(`/products/${encodeURIComponent(id)}/price-history`);
  },

  /**
   * Submit a user "report an issue with this listing" (#120). Lands in the
   * human review queue. Backed by `POST /api/products/:id/report`.
   */
  reportIssue(
    id: string,
    description: string,
    listingId?: string
  ): Promise<{ reportId: string; status: string }> {
    return api.post<{ reportId: string; status: string }>(
      `/products/${encodeURIComponent(id)}/report`,
      listingId ? { description, listingId } : { description }
    );
  },
};

export default productsApi;
